import { Head, Link, router, usePage, usePoll } from "@inertiajs/react"
import {
  CalendarDays,
  Clock,
  Copy,
  ExternalLink,
  Globe,
  Radio,
  Video,
} from "lucide-react"
import { useState } from "react"

import { ClassTime } from "@/components/events/class-time"
import GlowEventCard from "@/components/live-online/glow-event-card"
import { HomeScreenNotificationsDialog } from "@/components/member/home-screen-notifications-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { useEventClock } from "@/hooks/use-event-clock"
import MemberLayout from "@/layouts/member-layout"
import { useViewerTimeZone } from "@/lib/class-time"
import { shortModuleDate } from "@/lib/module-registration"
import { formatEventLocalTimeLabel } from "@/lib/timezone"
import type { WebPushPageProps } from "@/lib/web-push"
import {
  memberPath,
  memberSubscribeCheckoutPath,
  memberSubscribePath,
  settingsSubscriptionPath,
} from "@/routes"
import type {
  BreadcrumbItem,
  ModuleSession,
  Plan,
  SubscriptionData,
} from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Live Online",
    href: memberPath(),
  },
]

const LIVE_SCHEDULE_REFRESH_INTERVAL_MS = 30_000

export interface LiveClassEvent {
  id: number
  title: string
  slug: string
  excerpt: string | null
  start_date: string | null
  date_range: string | null
  location: string | null
  timezone: string
  timezone_display: string
  image_url: string | null
  formatted_start_time: string | null
  start_time_raw: string | null
  end_time_raw?: string | null
  formatted_time_range?: string | null
  duration_minutes: number | null
  is_recurring: boolean
  formatted_schedule: string | null
  zoom_enabled: boolean
  zoom_meeting_id?: string | null
  zoom_password?: string | null
  zoom_join_url?: string | null
  zoom_web_url?: string | null
  zoom_session_kind?: "meeting" | "webinar"
  live_now: boolean
  passed: boolean
  time_until_live_text: string | null
}

interface MyNextClass {
  program_title: string
  module_title: string
  module_href: string
  join_href: string | null
  location: string | null
  timezone_identifier: string | null
  session: ModuleSession
}

interface Props {
  subscription: SubscriptionData | null
  plans: Plan[]
  next_live_class: LiveClassEvent | null
  upcoming_schedule: LiveClassEvent[]
  my_next_class?: MyNextClass | null
  can_access_live_online: boolean
  needs_payment_update: boolean
  web_push: WebPushPageProps
}

// Live Class Hero Section
export function LiveClassHero({
  event,
  canAccess,
  showGlow = true,
}: {
  event: LiveClassEvent
  canAccess: boolean
  showGlow?: boolean
}) {
  const isLive = event.live_now
  const isPassed = event.passed
  const [copied, setCopied] = useState<"meeting" | "password" | null>(null)
  const { auth } = usePage<{
    auth: { user: { name: string; email: string } }
  }>().props

  const copyToClipboard = (text: string, type: "meeting" | "password") => {
    void navigator.clipboard.writeText(text)
    setCopied(type)
    setTimeout(() => setCopied(null), 2000)
  }

  const timeLabel = formatEventLocalTimeLabel({
    startTimeRaw: event.start_time_raw,
    formattedStartTime: event.formatted_start_time,
  })
  const joinableZoomUrl = event.zoom_join_url ?? event.zoom_web_url

  return (
    <div>
      <div className="border-sidebar-border/50 border-b pb-6">
        <div className="flex items-center gap-2">
          <Radio className="size-5 text-red-600" />
          <h2 className="text-2xl font-semibold tracking-tight">
            Daily Live Online
          </h2>
        </div>
      </div>

      <div className="pt-6">
        <GlowEventCard
          title={event.title}
          description={event.excerpt}
          badge={
            isPassed ? (
              <Badge className="bg-slate-500/80 text-white hover:bg-slate-500/80">
                Ended
              </Badge>
            ) : isLive ? (
              <Badge className="animate-pulse bg-red-500 text-white hover:bg-red-600">
                <Radio className="mr-1 h-3 w-3" />
                LIVE NOW
              </Badge>
            ) : (
              <Badge className="bg-white/20 text-white hover:bg-white/30">
                <Video className="mr-1 h-3 w-3" />
                {event.time_until_live_text ?? "Upcoming"}
              </Badge>
            )
          }
          scheduleText={event.is_recurring ? event.formatted_schedule : null}
          metaClassName="flex flex-col gap-2 text-sm text-white/80"
          meta={
            <>
              {event.date_range && (
                <div className="flex flex-wrap items-center gap-2">
                  <CalendarDays className="size-4" />
                  <span className="text-base font-semibold text-white">
                    {event.date_range}
                  </span>
                </div>
              )}

              {timeLabel && (
                <div className="flex flex-wrap items-center gap-2">
                  <Clock className="size-4" />
                  <span className="text-base font-semibold text-white">
                    {timeLabel}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-white/70">
                <Globe className="size-4" />
                <span>{event.timezone_display}</span>
              </div>
            </>
          }
          actions={
            isPassed ? undefined : canAccess ? (
              <>
                {event.zoom_enabled && joinableZoomUrl ? (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className={
                          isLive
                            ? "bg-red-500 text-white shadow-lg shadow-red-500/30 hover:bg-red-600"
                            : "bg-white shadow-lg hover:bg-white/90"
                        }
                      >
                        <Video className="mr-2 h-4 w-4" />
                        {isLive ? "Join Live Now" : "Access"}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Join Zoom Meeting</DialogTitle>
                        <DialogDescription>
                          Choose how you&apos;d like to join the live class
                        </DialogDescription>
                      </DialogHeader>

                      <div className="space-y-4 py-4">
                        <div className="flex flex-col gap-3">
                          <Button asChild className="w-full" size="lg">
                            <a
                              href={joinableZoomUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="mr-2 size-4" />
                              Open in Zoom App
                            </a>
                          </Button>

                          {event.zoom_web_url && (
                            <Button
                              asChild
                              variant="outline"
                              className="w-full"
                            >
                              <a
                                href={event.zoom_web_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Globe className="mr-2 size-4" />
                                Join in Browser
                              </a>
                            </Button>
                          )}
                        </div>

                        <Separator />

                        <div className="space-y-3">
                          <h4 className="font-medium">Meeting Details</h4>

                          {event.zoom_meeting_id && (
                            <div className="bg-muted flex items-center justify-between rounded-md p-3">
                              <div>
                                <p className="text-muted-foreground text-xs">
                                  Meeting ID
                                </p>
                                <p className="font-mono text-sm">
                                  {event.zoom_meeting_id}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  copyToClipboard(
                                    event.zoom_meeting_id!,
                                    "meeting",
                                  )
                                }
                              >
                                <Copy
                                  className={`size-4 ${copied === "meeting" ? "text-green-500" : ""}`}
                                />
                              </Button>
                            </div>
                          )}

                          {event.zoom_password && (
                            <div className="bg-muted flex items-center justify-between rounded-md p-3">
                              <div>
                                <p className="text-muted-foreground text-xs">
                                  Password
                                </p>
                                <p className="font-mono text-sm">
                                  {event.zoom_password}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  copyToClipboard(
                                    event.zoom_password!,
                                    "password",
                                  )
                                }
                              >
                                <Copy
                                  className={`size-4 ${copied === "password" ? "text-green-500" : ""}`}
                                />
                              </Button>
                            </div>
                          )}
                        </div>

                        <p className="text-muted-foreground text-xs">
                          Joining as: {auth?.user?.name || "Guest"}
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <Button
                    size="lg"
                    disabled
                    className="bg-white/50 text-purple-700 shadow-lg"
                  >
                    <Video className="mr-2 h-4 w-4" />
                    Not Available
                  </Button>
                )}
              </>
            ) : (
              <Button
                asChild
                size="lg"
                className="bg-white text-black shadow-lg hover:bg-white/90"
              >
                <Link href={memberSubscribePath()}>Subscribe</Link>
              </Button>
            )
          }
          imageUrl={event.image_url}
          imageContainerClassName="pb-4 lg:pb-4"
          reversed
          glowVariant={showGlow ? "mystic" : "none"}
        />
      </div>
    </div>
  )
}

// No Live Class Placeholder
function NoLiveClassPlaceholder({
  scheduleIsEmpty,
}: {
  scheduleIsEmpty: boolean
}) {
  return (
    <div className="relative">
      <Card className="relative overflow-hidden border-0 text-white">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/Leg_Stretch.jpg)" }}
        />

        {/* Black overlay */}
        <div className="absolute inset-0 bg-black/30" />

        {/* Content */}
        <CardContent className="relative flex min-h-[300px] items-center justify-center px-6 md:min-h-[500px] md:p-8">
          <div className="flex flex-col items-center space-y-4 text-center">
            {!scheduleIsEmpty && (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10">
                <Video className="h-8 w-8 text-white/60" />
              </div>
            )}
            <div className="space-y-2">
              <h2 className="text-xl font-semibold xl:text-3xl">
                {scheduleIsEmpty
                  ? "Live Schedule Announced Soon"
                  : "No Live Classes Today"}
              </h2>
              {!scheduleIsEmpty && (
                <p className="max-w-md text-sm text-white/90">
                  There are no live classes scheduled for today. Check the
                  upcoming schedule below.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function UpcomingScheduleSection({ events }: { events: LiveClassEvent[] }) {
  return (
    <section
      id="upcoming-schedule"
      className="border-sidebar-border/50 space-y-4 border-t pt-6"
    >
      <div className="border-sidebar-border/50 border-b pb-6">
        <div className="flex items-center gap-2">
          <Video className="size-5" />
          <h2 className="text-2xl font-semibold tracking-tight">
            Upcoming Daily Live Online
          </h2>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {events.map((event) => (
          <UpcomingScheduleCard key={event.id} event={event} />
        ))}
      </div>
    </section>
  )
}

function UpcomingScheduleCard({ event }: { event: LiveClassEvent }) {
  const timeLabel = formatEventLocalTimeLabel({
    startTimeRaw: event.start_time_raw,
    formattedStartTime: event.formatted_start_time,
  })

  return (
    <GlowEventCard
      title={event.title}
      description={event.excerpt}
      badge={
        <Badge className="bg-white/20 text-white hover:bg-white/30">
          <Clock className="mr-1.5 size-3.5" />
          {event.time_until_live_text ?? "Upcoming"}
        </Badge>
      }
      scheduleText={event.is_recurring ? event.formatted_schedule : null}
      meta={
        <>
          {event.date_range && (
            <div className="flex flex-wrap items-center gap-2">
              <CalendarDays className="size-4" />
              <span className="text-base font-semibold text-white">
                {event.date_range}
              </span>
            </div>
          )}

          {timeLabel && (
            <div className="flex flex-wrap items-center gap-2">
              <Clock className="size-4" />
              <span className="text-base font-semibold text-white">
                {timeLabel}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 text-white/70">
            <Globe className="size-4" />
            <span>{event.timezone_display}</span>
          </div>
        </>
      }
      metaClassName="flex flex-col gap-2 text-sm text-white/80"
      imageUrl={event.image_url}
      imageContainerClassName="pb-4 lg:pb-4"
      reversed
      glowVariant="none"
    />
  )
}

// Joining opens this long before a class starts.
const JOIN_LEAD_MS = 30 * 60 * 1000

// The member's own program class: in progress, or their next one.
function MyNextClassCard({ nextClass }: { nextClass: MyNextClass }) {
  const now = useEventClock()
  const viewerTimeZone = useViewerTimeZone()
  const starts = Date.parse(nextClass.session.starts_at ?? "")
  const live = starts <= now
  const joinOpen = nextClass.join_href && starts - now <= JOIN_LEAD_MS

  return (
    <Card className={live ? "border-red-500/40" : undefined}>
      <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-1">
          <p className="text-muted-foreground flex items-center gap-2 text-xs font-medium">
            {live ? (
              <Badge className="bg-red-500/15 text-red-700 hover:bg-red-500/15 dark:text-red-300">
                Live now
              </Badge>
            ) : (
              "Your next class"
            )}
            <span className="truncate">{nextClass.program_title}</span>
          </p>
          <h2 className="text-lg font-semibold">{nextClass.module_title}</h2>
          <p className="text-sm">
            {!live && `${shortModuleDate(nextClass.session.date)} · `}
            <ClassTime
              session={nextClass.session}
              schoolTimeZone={nextClass.timezone_identifier}
              viewerTimeZone={viewerTimeZone}
            />
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {joinOpen && (
            <Button asChild>
              <Link href={nextClass.join_href!}>
                <Video className="size-4" />
                Join class
              </Link>
            </Button>
          )}
          <Button variant={joinOpen ? "outline" : "default"} asChild>
            <Link href={nextClass.module_href}>Open module</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export default function MemberHome({
  subscription,
  next_live_class,
  upcoming_schedule,
  my_next_class,
  can_access_live_online,
  needs_payment_update,
  web_push,
}: Props) {
  usePoll(LIVE_SCHEDULE_REFRESH_INTERVAL_MS, {
    only: ["next_live_class", "upcoming_schedule", "my_next_class"],
  })

  const scheduleIsEmpty = !next_live_class && upcoming_schedule.length === 0

  return (
    <MemberLayout breadcrumbs={breadcrumbs}>
      <Head title="Live Online" />
      <HomeScreenNotificationsDialog webPush={web_push} />
      <div className="flex h-full flex-1 flex-col gap-6 p-4 md:p-6">
        {needs_payment_update && (
          <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
            <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-semibold text-amber-900 dark:text-amber-200">
                  Add your payment method
                </h3>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Your access is active. Add a card in secure Stripe checkout so
                  billing continues after your current period.
                </p>
              </div>
              <Button
                type="button"
                onClick={() => {
                  if (subscription?.plan_id != null) {
                    // Full page load — Inertia Link would XHR-follow the 302 to Stripe and hit CORS.
                    window.location.assign(
                      memberSubscribeCheckoutPath(subscription.plan_id),
                    )
                  } else {
                    router.visit(settingsSubscriptionPath())
                  }
                }}
              >
                {subscription?.plan_id != null
                  ? "Add payment method"
                  : "Open subscription settings"}
              </Button>
            </CardContent>
          </Card>
        )}

        {my_next_class && <MyNextClassCard nextClass={my_next_class} />}

        {/* Live Class Hero - Show for all users */}
        {next_live_class ? (
          <LiveClassHero
            event={next_live_class}
            canAccess={can_access_live_online}
            showGlow={false}
          />
        ) : (
          <NoLiveClassPlaceholder scheduleIsEmpty={scheduleIsEmpty} />
        )}

        {upcoming_schedule.length > 0 && (
          <UpcomingScheduleSection events={upcoming_schedule} />
        )}
      </div>
    </MemberLayout>
  )
}
